/**
 * 支付弹窗组件：显示扫码支付二维码并轮询支付状态
 */

import type { ReactElement } from 'react'
import { useEffect, useCallback, useState, useRef } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import {
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  Smartphone,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useCreateOrder, useOrderStatus } from '@/hooks/useOrders'
import { useAuthStore } from '@/stores'
import type { Package } from '@/types/package'
import type { PayType } from '@/types/order'

/** 轮询间隔（毫秒） */
const POLLING_INTERVAL_MS = 5000

/** 订单超时时间（毫秒）- 15分钟 */
const ORDER_TIMEOUT_MS = 15 * 60 * 1000
const CLIENT_SEAT_MIN = 1
const CLIENT_SEAT_MAX = 20
const CLIENT_SEAT_MONTHS_MIN = 1
const CLIENT_SEAT_MONTHS_MAX = 12
const CLIENT_SEAT_DAYS_PER_MONTH = 30

function normalizeClientSeatQuantity(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return CLIENT_SEAT_MIN
  }
  const quantity = Math.trunc(value)
  if (quantity < CLIENT_SEAT_MIN) {
    return CLIENT_SEAT_MIN
  }
  if (quantity > CLIENT_SEAT_MAX) {
    return CLIENT_SEAT_MAX
  }
  return quantity
}

function normalizeClientSeatPurchaseMonths(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return CLIENT_SEAT_MONTHS_MIN
  }
  const purchaseMonths = Math.trunc(value)
  if (purchaseMonths < CLIENT_SEAT_MONTHS_MIN) {
    return CLIENT_SEAT_MONTHS_MIN
  }
  if (purchaseMonths > CLIENT_SEAT_MONTHS_MAX) {
    return CLIENT_SEAT_MONTHS_MAX
  }
  return purchaseMonths
}

/**
 * 支付弹窗 Props
 */
interface PaymentDialogProps {
  /** 是否打开弹窗 */
  open: boolean
  /** 关闭弹窗回调 */
  onOpenChange: (open: boolean) => void
  /** 选中的套餐 */
  selectedPackage: Package | null
  /** 自定义购买算力（仅算力包） */
  customCredits?: number | null
  /** 购买席位数量（仅客户端席位包） */
  seatQuantity?: number | null
  /** 购买月数（仅客户端席位包） */
  purchaseMonths?: number | null
  /** 支付成功回调 */
  onPaymentSuccess?: () => void
}

/**
 * 支付状态类型
 */
type PaymentState =
  | 'idle'
  | 'creating'
  | 'pending'
  | 'polling'
  | 'success'
  | 'failed'
  | 'timeout'
  | 'cancelled'

/**
 * 格式化剩余时间
 * @param ms 剩余毫秒数
 * @returns 格式化的时间字符串
 */
function formatRemainingTime(ms: number): string {
  if (ms <= 0) {
    return '00:00'
  }
  const minutes = Math.floor(ms / 60000)
  const seconds = Math.floor((ms % 60000) / 1000)
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

/**
 * 支付弹窗组件
 * @param props 组件属性
 * @returns 弹窗 ReactElement
 */
export function PaymentDialog({
  open,
  onOpenChange,
  selectedPackage,
  customCredits = null,
  seatQuantity = null,
  purchaseMonths = null,
  onPaymentSuccess,
}: PaymentDialogProps): ReactElement {
  const refreshUser = useAuthStore((state) => state.refreshUser)

  const createOrderMutation = useCreateOrder()

  const [paymentState, setPaymentState] = useState<PaymentState>('idle')
  const [orderId, setOrderId] = useState<string | null>(null)
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null)
  const [remainingTime, setRemainingTime] = useState<number>(ORDER_TIMEOUT_MS)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [payType, setPayType] = useState<PayType>('wechat')
  const clientSeatQuantity = normalizeClientSeatQuantity(seatQuantity)
  const clientSeatPurchaseMonths = normalizeClientSeatPurchaseMonths(purchaseMonths)
  const isSeatPackage = selectedPackage?.packageType === 'client_seat' ||
    selectedPackage?.packageType === 'client_plus'

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // 轮询订单状态
  const { data: orderStatusData } = useOrderStatus(
    orderId,
    paymentState === 'pending' || paymentState === 'polling',
    POLLING_INTERVAL_MS
  )

  /**
   * 重置状态
   */
  const resetState = useCallback((): void => {
    setPaymentState('idle')
    setOrderId(null)
    setPaymentUrl(null)
    setRemainingTime(ORDER_TIMEOUT_MS)
    setErrorMessage(null)
    setPayType('wechat')
    if (timerRef.current !== null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  /**
   * 创建订单
   */
  const handleCreateOrder = useCallback(async (): Promise<void> => {
    if (selectedPackage === null) {
      return
    }

    setPaymentState('creating')
    setErrorMessage(null)

    try {
      const payload = isSeatPackage
        ? {
            packageId: selectedPackage.id,
            quantity: clientSeatQuantity,
            purchaseMonths: clientSeatPurchaseMonths,
            payType,
          }
        : selectedPackage.packageType === 'recharge' && customCredits !== null
          ? { packageId: selectedPackage.id, customCredits, payType }
        : { packageId: selectedPackage.id, payType }

      const response = await createOrderMutation.mutateAsync(payload)

      setOrderId(response.orderId)
      setPaymentUrl(response.paymentUrl ?? response.codeUrl ?? null)
      if (response.payType !== undefined) {
        setPayType(response.payType)
      }
      setPaymentState('pending')

      // 计算剩余时间
      const expireTime = new Date(response.expireAt).getTime()
      const now = Date.now()
      setRemainingTime(Math.max(0, expireTime - now))
    } catch (error) {
      setPaymentState('failed')
      setErrorMessage(
        error instanceof Error ? error.message : '创建订单失败，请重试'
      )
    }
  }, [selectedPackage, isSeatPackage, clientSeatQuantity, clientSeatPurchaseMonths, customCredits, payType, createOrderMutation])

  // 监听订单状态变化（轮询结果回调）
  /* eslint-disable react-hooks/set-state-in-effect -- 此处是对外部轮询结果的响应，需要同步更新状态 */
  useEffect((): void => {
    if (orderStatusData === undefined) {
      return
    }

    if (orderStatusData.status === 'paid') {
      setPaymentState('success')
      // 刷新用户信息以更新余额
      void refreshUser()
      // 触发成功回调
      onPaymentSuccess?.()
    } else if (orderStatusData.status === 'failed') {
      setPaymentState('failed')
      setErrorMessage('支付失败，请重试')
    } else if (orderStatusData.status === 'cancelled') {
      setPaymentState('cancelled')
    }
  }, [orderStatusData, refreshUser, onPaymentSuccess])
  /* eslint-enable react-hooks/set-state-in-effect */

  // 倒计时逻辑
  useEffect((): (() => void) => {
    if (paymentState !== 'pending' && paymentState !== 'polling') {
      return (): void => {
        // cleanup
      }
    }

    timerRef.current = setInterval((): void => {
      setRemainingTime((prev): number => {
        const newValue = prev - 1000
        if (newValue <= 0) {
          setPaymentState('timeout')
          if (timerRef.current !== null) {
            clearInterval(timerRef.current)
            timerRef.current = null
          }
          return 0
        }
        return newValue
      })
    }, 1000)

    return (): void => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [paymentState])

  // 弹窗关闭时重置状态
  useEffect((): (() => void) | undefined => {
    if (!open) {
      // 延迟重置，避免动画中途状态变化
      const timer = setTimeout(resetState, 300)
      return (): void => {
        clearTimeout(timer)
      }
    }
    return undefined
  }, [open, resetState])

  /**
   * 处理关闭弹窗
   */
  const handleClose = useCallback((): void => {
    onOpenChange(false)
  }, [onOpenChange])

  /**
   * 处理重新支付
   */
  const handleRetry = useCallback((): void => {
    resetState()
    void handleCreateOrder()
  }, [resetState, handleCreateOrder])

  /**
   * 渲染二维码区域
   */
  function renderQRCode(): ReactElement {
    if (paymentUrl === null) {
      return (
        <div className="flex h-64 w-64 items-center justify-center rounded-xl bg-muted">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )
    }

    return (
      <div className="relative">
        <div className="overflow-hidden rounded-xl border-2 border-primary/20 bg-white p-4">
          <QRCodeSVG
            value={paymentUrl}
            size={224}
            level="H"
            marginSize={0}
          />
        </div>
      </div>
    )
  }

  const payTypeLabelMap: Record<PayType, string> = {
    wechat: '微信',
    alipay: '支付宝',
    unionpay: '云闪付',
  }

  const payTypeOptions: readonly { value: PayType; label: string }[] = [
    { value: 'wechat', label: '微信支付' },
    { value: 'alipay', label: '支付宝' },
    { value: 'unionpay', label: '云闪付' },
  ]

  /**
   * 渲染支付状态内容
   */
  function renderContent(): ReactElement {
    const isCustomRecharge = selectedPackage?.packageType === 'recharge' && customCredits !== null
    const customRechargeAmountYuan = isCustomRecharge
      ? (customCredits / 100) * selectedPackage.customPricePer100 / 100
      : 0
    const clientSeatTotalAmountYuan = isSeatPackage && selectedPackage !== null
      ? selectedPackage.priceYuan * clientSeatQuantity * clientSeatPurchaseMonths
      : 0

    switch (paymentState) {
      case 'idle':
        return (
          <div className="space-y-4 py-2">
            {selectedPackage !== null ? (
              <div className="w-full rounded-lg border bg-muted/30 p-4 space-y-3">
                <div className="space-y-2">
                  <span className="text-sm text-muted-foreground">支付渠道</span>
                  <div className="grid grid-cols-3 gap-2">
                    {payTypeOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={(): void => {
                          setPayType(option.value)
                        }}
                        className={cn(
                          'rounded border px-2 py-1.5 text-sm transition-colors',
                          payType === option.value
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-background text-foreground'
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">套餐</span>
                  <span className="font-medium">{selectedPackage.name}</span>
                </div>

                {isSeatPackage ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">席位数量</span>
                      <span className="font-medium">{clientSeatQuantity}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">购买月数</span>
                      <span className="font-medium">
                        {clientSeatPurchaseMonths} 个月（{clientSeatPurchaseMonths * CLIENT_SEAT_DAYS_PER_MONTH} 天）
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">总金额</span>
                      <span className="text-lg font-bold text-primary">
                        ¥{clientSeatTotalAmountYuan.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ) : isCustomRecharge ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">金额</span>
                      <span className="text-lg font-bold text-primary">
                        ¥{customRechargeAmountYuan.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">到账算力</span>
                      <span className="font-medium">
                        {customCredits.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">其中赠送</span>
                      <span className="font-medium">0</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">金额</span>
                      <span className="text-lg font-bold text-primary">
                        ¥{selectedPackage.priceYuan}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">到账算力</span>
                      <span className="font-medium">
                        {selectedPackage.totalCredits.toLocaleString()}
                      </span>
                    </div>
                    {selectedPackage.bonusCredits > 0 ? (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">其中赠送</span>
                        <span className="font-medium text-emerald-600">
                          {selectedPackage.bonusCredits.toLocaleString()}
                        </span>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}

            <Button className="w-full" onClick={(): void => { void handleCreateOrder() }}>
              确认并创建订单
            </Button>
          </div>
        )

      case 'creating':
        return (
          <div className="flex flex-col items-center gap-6 py-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-muted-foreground">正在创建订单...</p>
          </div>
        )

      case 'pending':
      case 'polling':
        return (
          <div className="flex flex-col items-center gap-6">
            {renderQRCode()}

            <div className="flex items-center gap-2 text-muted-foreground">
              <Smartphone className="h-4 w-4" />
              <span className="text-sm">请使用{payTypeLabelMap[payType]}扫码支付</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">支付剩余时间：</span>
              <span
                className={cn(
                  'font-mono font-medium',
                  remainingTime < 60000 ? 'text-destructive' : 'text-foreground'
                )}
              >
                {formatRemainingTime(remainingTime)}
              </span>
            </div>

            {selectedPackage !== null ? (
              <div className="w-full rounded-lg border bg-muted/30 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">套餐</span>
                  <span className="font-medium">{selectedPackage.name}</span>
                </div>
                {isSeatPackage ? (
                  <>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">席位数量</span>
                      <span className="font-medium">{clientSeatQuantity}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">购买月数</span>
                      <span className="font-medium">
                        {clientSeatPurchaseMonths} 个月（{clientSeatPurchaseMonths * CLIENT_SEAT_DAYS_PER_MONTH} 天）
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">总金额</span>
                      <span className="text-lg font-bold text-primary">
                        ¥{clientSeatTotalAmountYuan.toFixed(2)}
                      </span>
                    </div>
                  </>
                ) : isCustomRecharge ? (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">金额</span>
                      <span className="text-lg font-bold text-primary">
                        ¥{customRechargeAmountYuan.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">到账算力</span>
                      <span className="font-medium">
                        {customCredits.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">其中赠送</span>
                      <span className="font-medium">0</span>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">金额</span>
                      <span className="text-lg font-bold text-primary">
                        ¥{selectedPackage.priceYuan}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">到账算力</span>
                      <span className="font-medium">
                        {selectedPackage.totalCredits.toLocaleString()}
                      </span>
                    </div>
                    {selectedPackage.bonusCredits > 0 ? (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">其中赠送</span>
                        <span className="font-medium text-emerald-600">
                          {selectedPackage.bonusCredits.toLocaleString()}
                        </span>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )

      case 'success':
        return (
          <div className="flex flex-col items-center gap-6 py-8">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-500/10">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold">支付成功</h3>
              <p className="mt-2 text-muted-foreground">
                套餐已生效，感谢您的购买！
              </p>
            </div>
            <Button onClick={handleClose} className="w-full max-w-xs">
              完成
            </Button>
          </div>
        )

      case 'failed':
      case 'cancelled':
        return (
          <div className="flex flex-col items-center gap-6 py-8">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-10 w-10 text-destructive" />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold">
                {paymentState === 'cancelled' ? '支付已取消' : '支付失败'}
              </h3>
              <p className="mt-2 text-muted-foreground">
                {errorMessage ?? '请重新尝试支付'}
              </p>
            </div>
            <div className="flex w-full max-w-xs gap-3">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                关闭
              </Button>
              <Button onClick={handleRetry} className="flex-1">
                <RefreshCw className="mr-2 h-4 w-4" />
                重试
              </Button>
            </div>
          </div>
        )

      case 'timeout':
        return (
          <div className="flex flex-col items-center gap-6 py-8">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-amber-500/10">
              <Clock className="h-10 w-10 text-amber-500" />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold">订单已超时</h3>
              <p className="mt-2 text-muted-foreground">
                支付时间已过期，请重新下单
              </p>
            </div>
            <div className="flex w-full max-w-xs gap-3">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                关闭
              </Button>
              <Button onClick={handleRetry} className="flex-1">
                <RefreshCw className="mr-2 h-4 w-4" />
                重新下单
              </Button>
            </div>
          </div>
        )

      default:
        return (
          <div className="flex flex-col items-center gap-6 py-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-muted-foreground">加载中...</p>
          </div>
        )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {paymentState === 'success'
              ? '支付完成'
              : paymentState === 'failed' || paymentState === 'cancelled'
                ? '支付异常'
                : paymentState === 'timeout'
                  ? '订单超时'
                  : paymentState === 'idle'
                    ? '确认订单'
                    : '扫码支付'}
          </DialogTitle>
          {paymentState === 'pending' || paymentState === 'polling' ? (
            <DialogDescription>
              扫描二维码完成支付
            </DialogDescription>
          ) : null}
        </DialogHeader>

        {renderContent()}
      </DialogContent>
    </Dialog>
  )
}
